import { Stack, Typography } from "@mui/material";
import { useFhirData } from "../../../utils/useFhirData";
import { useFormatter } from "../../../utils/useFormatter";

interface IProps {
  location: fhir5.Location;
  organization?: any;
}

function isVirtualService(location: fhir5.Location) {
  return !!location?.extension?.find(
    (ext) =>
      ext.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/virtual-service" &&
      ext.valueBoolean === true
  );
}

/** Lê a extension attendance-channel da Location. */
function getAttendanceChannel(location: fhir5.Location): string | undefined {
  return (location?.extension?.find((ext) =>
    ext.url?.includes("attendance-channel")
  ) as any)?.valueCode;
}

/** Retorna o rótulo legível do tipo de atendimento com base nas extensions da Location. */
function getAttendanceLabel(location: fhir5.Location): string {
  const channel = getAttendanceChannel(location);
  if (channel === "domicilio")       return "Atendimento a Domicílio";
  if (channel === "chat")            return "Chat";
  if (channel === "teleatendimento") return "Teleatendimento";
  // Retrocompatibilidade: locations antigas com virtual-service=true sem canal definido
  if (isVirtualService(location))    return "Teleatendimento";
  return "Presencial";
}

export function LocationInfo({ location, organization }: IProps) {
  const brandLogoOrganization = useFhirData?.brandLogoOrganization(
    organization?.resource?.extension
  );

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        gap: "12px",
      }}
      direction="row"
    >
      {/* Logo/Avatar da organização - tamanho reduzido para compactar */}
      <Stack
        justifyContent="center"
        alignItems="center"
        width="70px"
        height="70px"
        borderRadius={2}
        sx={{ borderColor: "primary400.main", flexShrink: 0 }}
      >
        {brandLogoOrganization ? (
          <img alt="Brand Logo" width={"100%"} src={brandLogoOrganization} />
        ) : (
          <Typography variant="h5" fontWeight={700} fontFamily="fantasy">
            {useFormatter.formatNameFirstInitials(
              organization?.resource?.alias?.[0],
              2
            )}
          </Typography>
        )}
      </Stack>
      <Stack spacing={1}>
        {/* Nome da organização - fonte menor para compactar */}
        <Typography
          sx={{ color: "neutral1000.main", wordBreak: "break-word" }}
          variant="h6"
          fontWeight={600}
        >
          {organization?.resource?.alias?.[0] ||
            organization?.resource?.name ||
            "-"}
        </Typography>

        {/* Informações de serviço - espaçamento reduzido */}
        <Stack width="100%" direction="row" alignItems="start" gap="12px">
          <Typography variant="body2" color="neutral700.main" textAlign="left">
            Atendimento
          </Typography>
          <Typography
            variant="body2"
            color="neutral1000.main"
            flex={1}
            fontWeight={500}
          >
            {getAttendanceLabel(location)}
          </Typography>
        </Stack>

        {/* Localização — oculta para virtual e domicílio (endereço não é fixo nesses casos) */}
        {!isVirtualService(location) && getAttendanceChannel(location) !== "domicilio" && (
          <Stack width="100%" direction="row" alignItems="start" gap="12px">
            <Typography
              variant="body2"
              color="neutral700.main"
              textAlign="left"
              sx={{
                wordBreak: "break-word",
                textTransform: "capitalize",
              }}
            >
              Localização
            </Typography>
            <Typography
              variant="body2"
              color="neutral1000.main"
              flex={1}
              fontWeight={500}
              sx={{
                wordBreak: "break-word",
                textTransform: "capitalize",
              }}
            >
              {/* TODO: Criar função geradora de endereço */}
              {location.address?.line?.join(", ")}
            </Typography>
          </Stack>
        )}

        {/* Contato - espaçamento reduzido */}
        <Stack width="100%" direction="row" alignItems="start" gap="12px">
          <Typography
            variant="body2"
            color="neutral700.main"
            textAlign="left"
            sx={{
              wordBreak: "break-word",
              textTransform: "capitalize",
            }}
          >
            Contato
          </Typography>
          <Typography
            variant="body2"
            color="neutral1000.main"
            flex={1}
            fontWeight={500}
            sx={{
              wordBreak: "break-word",
              textTransform: "capitalize",
            }}
          >
            {location?.contact?.[0]?.telecom?.[0]?.value ||
              organization?.resource?.contact
                ?.find((e: any) => e?.telecom)
                ?.telecom?.find((el: any) => el?.system === "phone")?.value ||
              "-"}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}
